import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_NAME = 'GoLugati';
const SITE_URL = 'https://go-lugati.com';
const DEFAULT_DESCRIPTION =
  "Votre passerelle vers la langue arabe, le Coran et le patrimoine littéraire. Cours en ligne avec des professeurs certifiés, essai gratuit offert.";
const DEFAULT_IMAGE = `${SITE_URL}/icons/icon-512.png`;

// Une seule URL par route pour les 5 langues supportées (pas de /en/, /es/... dans
// l'URL) : og:locale reflète donc la langue affichée côté client au moment du
// partage, plutôt qu'une valeur fixe qui serait fausse pour 4 langues sur 5.
const OG_LOCALE_BY_LANG: Record<string, string> = {
  fr: 'fr_FR',
  en: 'en_US',
  ar: 'ar_AR',
  es: 'es_ES',
  de: 'de_DE',
};

interface SeoProps {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
}

export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const { i18n } = useTranslation();
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonical = `${SITE_URL}${path}`;
  const ogLocale = OG_LOCALE_BY_LANG[i18n.resolvedLanguage ?? 'fr'] ?? 'fr_FR';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

export { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
